import {FACTIONS} from '../constants/factions';

// Анимация ближнего боя: отряд делает рывок к цели и бьет слешем — оружие его
// фракции проходит дугой, оставляя световой след; на цели — вспышка, искры и
// всплывающее «−N» (свет удара — configs/combatCastAnimation). Все — цветом
// игрока-владельца бьющего отряда.
//
// ПОРЯДОК. Сначала бьют отряды игрока, чей сейчас ход, потом — отряды остальных
// участников боя. У отряда по одному удару на каждую цель, которой он нанес
// урон; цели одного отряда бьются по очереди, «волнами»: в первой волне все
// отряды бьют свою первую цель, во второй — вторую и так далее.
//
// ИТОГ БОЯ НА ПОЛЕ — ПОСЛЕ УДАРОВ. Урон на плашке отряда (нанесенный и
// полученный), значок скрещенных мечей и волна по ячейке появляются, когда
// отыграли все удары (см. HexGridRenderer.showMeleeAnimation). Чужой бой при
// этом укладывается в шаг темпа действий (см. pace ниже)

// Спрайт-лист оружия фракций: белые силуэты, красятся в цвет игрока. Ключ, путь
// и размер кадра должны совпадать с загрузкой в GameScene.preload(). Сам файл
// собирает developer_artifacts/scripts/generateMeleeEffectSprites.cjs — порядок
// кадров там тот же
//
// Стандартный меч: им бьет отряд без особого образа фракции (нарисован общим
// силуэтом), нейтральный отряд и отряд без фракции. Это не фракция: у Людей в
// образе свой гладиус
export const MELEE_STANDARD_WEAPON = 'standard';

export const MELEE_WEAPONS_SHEET = {
  key: 'melee_weapons',
  path: '/img/effects/melee/weapons.v2.png',
  frameWidth: 128,
  frameHeight: 128,
  // Поле вокруг оружия в кадре: оружие занимает кадр по высоте за вычетом
  // полей сверху и снизу и стоит по центру
  framePadding: 4,
  // Оружие в порядке кадров. Нарисовано и для фракций, которые еще не играбельны:
  // их подключение оружия не потребует
  frames: [
    FACTIONS.DWARVES, FACTIONS.ORCS, FACTIONS.VENTORY, FACTIONS.LEPRECHAUNS,
    FACTIONS.DEMONS, FACTIONS.BEASTMEN, FACTIONS.ELEMENTALS, FACTIONS.ELVES,
    FACTIONS.ENLIGHTENED, FACTIONS.GIANTS, FACTIONS.INSECTOIDS, FACTIONS.GOBLINS,
    FACTIONS.CELECTIALS, FACTIONS.RATMEN, FACTIONS.SUCCUBES, MELEE_STANDARD_WEAPON,
    FACTIONS.AQUARS, FACTIONS.DARK_ELVES, FACTIONS.TECHNODWARVES, FACTIONS.DRAGONS,
    FACTIONS.ENTS, FACTIONS.FENIX, FACTIONS.GREMLINS, FACTIONS.MUSHROOM_PEOPLE,
    FACTIONS.SPIRITS, FACTIONS.DOPPLERS, FACTIONS.UNDEAD, FACTIONS.WIZARDS,
    FACTIONS.HUMANS,
  ],
};

// Реализованные фракции, чьего оружия еще нет в спрайт-листе: их отряды бьют
// стандартным мечом. Появится кадр — перенести фракцию в MELEE_WEAPONS_SHEET.frames
export const FACTIONS_WITHOUT_OWN_MELEE_WEAPON = [];

/**
 * Кадр оружия фракции; фракции нет или она не задана — стандартный меч.
 * Положено ли отряду оружие его фракции, решает вызывающий (см.
 * playMeleeAnimation: только отряду с особым образом фракции)
 *
 * @param {string|null} [factionId]
 * @returns {number}
 */
export const resolveMeleeWeaponFrame = (factionId) => {
  const {frames} = MELEE_WEAPONS_SHEET;
  const frame = frames.indexOf(factionId);
  return frame >= 0 ? frame : frames.indexOf(MELEE_STANDARD_WEAPON);
};

// Звук взмаха оружия — по урону удара: чем сильнее удар, тем тяжелее звук.
// Ступени идут от сильных к слабым; ключи звуков — из scenes/sounds.js.
//
// skipMs — сколько тихого вступления файла пропустить. Звук запускается в
// начале удара, а попадание приходится на его 145-ю мс; у тяжелых звуков
// громкая часть начинается позже (у sward_flash_4 — с 260-й мс файла), и без
// пропуска сильный удар был бы сначала виден и только потом слышен. С
// пропуском громкая часть каждого звука начинается примерно на 80-й мс удара
// (замер уровня по окнам 20 мс: порог −12 дБ от пика — на 80, 120, 140 и
// 260-й мс файлов; пропускается только участок тише −27 дБ от пика)
export const MELEE_STRIKE_SOUNDS = [
  {minDamage: 10, sound: 'sward_flash_4', skipMs: 180},
  {minDamage: 6, sound: 'sward_flash_3', skipMs: 60},
  {minDamage: 3, sound: 'sward_flash_2', skipMs: 40},
  {minDamage: 1, sound: 'sward_flash_1', skipMs: 0},
];

/**
 * Ступень звука взмаха для удара с таким уроном
 *
 * @param {number} damage — урон удара по цели (не потеря численности: у
 *   отряда в крепости удар может потерь не дать, а взмах все равно звучит)
 * @returns {{minDamage: number, sound: string, skipMs: number}|null} null — урона нет
 */
export const resolveMeleeStrikeSoundTier = (damage) => (
  MELEE_STRIKE_SOUNDS.find((tier) => damage >= tier.minDamage) ?? null
);

/**
 * Каким звуком звучит взмах удара с таким уроном
 *
 * @param {number} damage
 * @returns {string|null} ключ звука; null — урона нет
 */
export const resolveMeleeStrikeSound = (damage) => resolveMeleeStrikeSoundTier(damage)?.sound ?? null;

// Текстуры, которые рисуются кодом (см. utils/effects/meleeEffectTextures):
// белые — красятся в цвет игрока
export const MELEE_TEXTURE_KEYS = {
  // Мягкое круглое свечение: искры удара и ореол вспышки
  GLOW: 'melee_glow',
  // Звезда вспышки попадания: яркое ядро с лучами
  BURST: 'melee_burst',
  // Капля крови (см. configs/bloodSplatter): плотный круг с мягким краем
  BLOOD_DROP: 'blood_drop',
  // Пятно крови на земле: неровная клякса с брызгами вокруг
  BLOOD_STAIN: 'blood_stain',
};

export const MELEE_ANIMATION = {
  // Один удар: рывок к цели, взмах, возврат. Времена — от начала удара
  strike: {
    // Через столько начинается следующая волна ударов той же стороны
    durationMs: 430,
    // Рывок бьющего отряда к цели и обратно (доля размера ячейки)
    lunge: {distanceFactor: 0.3, outMs: 110, holdMs: 110},
    // Взмах: оружие идет по дуге вокруг бьющего отряда, острием наружу.
    // Попадание — в середине взмаха, когда оружие проходит направление на цель
    slash: {
      startAtMs: 50,
      durationMs: 190,
      // Размах дуги, градусы: поровну в обе стороны от направления на цель
      sweepDeg: 130,
      // Докуда дотягивается острие — доля расстояния от бьющего (после рывка)
      // до цели: острие проходит по иконке цели
      reachShare: 1,
      // Длина оружия — доля размера ячейки
      weaponLengthFactor: 1,
      // Под цветным оружием лежит такое же белое, во столько раз крупнее:
      // светлая кромка, с которой темное оружие читается на темной подложке
      rimScale: 1.14,
      // Световой след — полумесяц по пройденной дуге: толстый в середине,
      // сходит на нет к краям. Толщина в середине — доля размера ячейки;
      // после взмаха след гаснет за fadeMs
      trailWidthFactor: 0.4,
      // Точек на край полумесяца: дуга рисуется ломаной
      trailSegments: 20,
      fadeMs: 190,
      // Слои следа: мягкое свечение шире самого следа и белое ядро тоньше него
      glowWidthShare: 1.9,
      glowAlpha: 0.3,
      coreWidthShare: 0.42,
    },
  },
  // Пауза между ударами первой стороны (ходящий игрок) и ответом остальных
  sideGapMs: 150,
  // Сколько после начала последней волны до показа итога на плашках: удар
  // успевает попасть, цель — отшатнуться
  revealAfterLastWaveMs: 400,
  impact: {
    // Цель отшатывается от удара и возвращается
    recoil: {distanceFactor: 0.18, ms: 85},
    // Цель кратко краснеет
    flashColor: 0xff2a2a,
    flashMs: 150,
  },
  // Всплывающее «−N»: красное в белом контуре, поднимается над целью и гаснет
  number: {
    // Откуда всплывает (доля размера ячейки над центром отряда) и на сколько
    startLiftFactor: 0.42,
    // Подъем с запасом: к следующей волне ударов число уходит выше, и новое
    // «−N» над той же целью на него не наезжает
    riseFactor: 0.85,
    durationMs: 850,
    // В сжатом чужом бою волны идут чаще, и число поднимается быстрее во
    // столько же раз — но не быстрее этого предела: иначе его не прочесть.
    // При пределе сжатия ударов (pace.minTimeFactor) число к следующей волне
    // все еще успевает уйти на свою высоту
    minTimeFactor: 0.6,
    // Короткое «выпрыгивание» в начале: с этого масштаба до обычного
    popFromScale: 0.5,
    popMs: 130,
    // С этой доли пути число начинает гаснуть
    fadeFromProgress: 0.55,
    fontSizeFactor: 0.56,
    // Шаг между числами, когда цель бьют сразу несколько отрядов (доля размера
    // ячейки): числа встают рядом и не сливаются в одно
    sideBySideFactor: 0.85,
    color: '#e0121b',
    strokeColor: '#ffffff',
    strokeWidthShare: 0.2,
  },
  // Цвет эффектов нейтрального отряда: его «цвет игрока» — чистый черный, а
  // черные след и вспышка не читаются. Стальной серый средней яркости виден
  // и на темных подложках поля, и на снежных с соляной (светло-серый с белой
  // кромкой оружия на них сливался с фоном)
  neutralColor: 0x8e949c,
  // Переброс костей: сначала звучат кости, потом удар (как у выстрела)
  rerollDelayMs: 500,
  // Чужой бой в живой партии (бот, соперник по сети) идет в темпе, который
  // задает не зритель: следующее действие придет через шаг темпа, и им может
  // оказаться завершение этапа — после него итог боя с поля уходит. Удары
  // сжимаются по времени так, чтобы уложиться и оставить итогу время побыть на
  // поле; если волн слишком много — цели каждого отряда бьются одной волной;
  // не помогло и это — анимации нет, итог виден сразу
  pace: {
    // Доля шага темпа, которая остается итогу боя после ударов
    resultHoldShare: 0.3,
    // Сильнее этого удары не сжимаются (доля обычной длительности)
    minTimeFactor: 0.45,
  },
  // Запас сверх расчетной длительности, после которого анимация считается
  // зависшей (скрытая вкладка: твины стоят, часы идут) и итог показывается
  staleMarginMs: 1500,
};
